import { useEffect } from 'react';

/**
 * App-wide iOS keyboard-dismiss behavior:
 *  1) Tap anywhere outside a form control → blur the focused input/textarea
 *  2) Readonly trick on textarea blur so the native ✓ in the iOS form accessory
 *     bar actually drops the keyboard (WebKit sometimes ignores plain blur on
 *     multiline fields).
 * No-op outside iOS.
 */

const FORM_CONTROL_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button',
  'a',
  'label',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="textbox"]',
  '[role="combobox"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="option"]',
].join(',');

function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSUA = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  return iOSUA || iPadOS;
}

function forceDismissTextarea(el) {
  if (!el || el.tagName !== 'TEXTAREA') { el?.blur?.(); return; }
  try {
    el.readOnly = true;
    el.blur();
  } finally {
    setTimeout(() => { try { el.readOnly = false; } catch {} }, 100);
  }
}

export default function useIosKeyboardDismiss() {
  useEffect(() => {
    if (!isIos()) return undefined;

    const handler = (e) => {
      const active = document.activeElement;
      if (!active) return;
      const tag = active.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
      if (active.readOnly || active.disabled) return;

      const target = e.target;
      if (target && typeof target.closest === 'function' && target.closest(FORM_CONTROL_SELECTOR)) return;

      if (tag === 'TEXTAREA') forceDismissTextarea(active);
      else active.blur();
    };

    document.addEventListener('pointerdown', handler, { capture: true, passive: true });
    return () => document.removeEventListener('pointerdown', handler, { capture: true });
  }, []);
}
